import { BusinessPurpose } from '@ebalance/proto/bfftypes/v1/business_purpose_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { z } from 'zod'

import { todayCivilDate } from '~/models/civilDate'
import {
  type CivilDateInput,
  civilDateSchema,
  registrationNumberSchema,
  validPeriodRefine,
  validPeriodRefineIssue,
} from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/** #7708: 自己託送専用コードは小売ライセンスを持っていても登録番号を要しない。 */
export function isRegistrationNumberRequired(
  licenseTypes: number[],
  purpose: number,
): boolean {
  return (
    licenseTypes.includes(LicenseType.RETAIL) &&
    purpose !== Number(BusinessPurpose.SELF_WHEELING)
  )
}

function businessMasterRegistrationNumberRequiredForRetailRefine(v: {
  licenseTypes: number[]
  purpose: number
  registrationNumber: string
}): boolean {
  return (
    !isRegistrationNumberRequired(v.licenseTypes, v.purpose) ||
    v.registrationNumber !== ''
  )
}

const businessMasterRegistrationNumberRequiredForRetailIssue: {
  message: string
  path: string[]
} = {
  message:
    '小売ライセンスを持つ事業者は、登録小売電気事業者 登録番号を入力してください',
  path: ['registrationNumber'],
}

/**
 * BusinessMaster の Create / Edit 共通 Zod schema。
 *
 * businessCode の format 制約 (^[0-9A-Z]{4}$) は backend domain の
 * valueobject.ParseBusinessCode (OCCTO 事業者コード = 半角英数字4桁・大文字) と同期。
 *
 * purpose は number で受ける (nativeEnum + refine は output 型を narrowing して
 * RHF useForm の型推論と衝突するため)。
 */
export const BusinessMasterFormSchema = z
  .object({
    businessCode: z
      .string()
      .min(1, 'コードを入力してください')
      .regex(
        /^[0-9A-Z]{4}$/,
        'コードは半角英数字4桁 (大文字) で入力してください',
      ),
    businessName: z
      .string()
      .min(1, '名称を入力してください')
      .max(50, '名称は 50文字以内で入力してください'),
    licenseTypes: z
      .array(z.number().int())
      .min(1, 'ライセンス区分を1つ以上選択してください'),
    purpose: z
      .number()
      .int()
      .min(BusinessPurpose.NORMAL, '取得目的を選択してください')
      .max(BusinessPurpose.SELF_WHEELING, '取得目的を選択してください'),
    validFrom: z.custom<CivilDateInput>(
      (v) => civilDateSchema.safeParse(v).success,
      { message: '適用開始日を入力してください' },
    ),
    validTo: civilDateSchema.optional(),
    registrationNumber: registrationNumberSchema,
  })
  .refine(validPeriodRefine, validPeriodRefineIssue)
  .refine(
    businessMasterRegistrationNumberRequiredForRetailRefine,
    businessMasterRegistrationNumberRequiredForRetailIssue,
  )

export type BusinessMasterFormValues = z.infer<typeof BusinessMasterFormSchema>

/**
 * テスト / storybook 用の valid な初期値生成ヘルパー。
 */
export function makeValidFormValues(
  overrides?: Partial<BusinessMasterFormValues>,
): BusinessMasterFormValues {
  return {
    businessCode: '1234',
    businessName: 'テスト事業者',
    licenseTypes: [1], // LicenseType.GENERATION
    purpose: BusinessPurpose.NORMAL,
    validFrom: todayCivilDate(),
    validTo: undefined,
    registrationNumber: '',
    ...overrides,
  }
}
